import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FloatingDock from '@/components/layout/FloatingDock';
import {
  Lock, Plus, Heart, Users, Calendar, Video, FileText, Image, Mic,
  ChevronRight, Edit2, Trash2, Eye, EyeOff, Shield, Clock, Gift,
  Cake, GraduationCap, Baby, Star, Sparkles, Send, X, Check,
  Archive, Unlock, AlertTriangle, Upload, Play, Pause, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Life moment icons mapping
const MOMENT_ICONS = {
  birthday: Cake,
  anniversary: Heart,
  graduation: GraduationCap,
  wedding: Heart,
  birth_child: Baby,
  retirement: Star,
  holiday: Gift,
  achievement: Star,
  memorial: Heart,
  custom: Calendar,
};

const TimeCapsule = ({ user }) => {
  const [capsules, setCapsules] = useState([]);
  const [stats, setStats] = useState(null);
  const [lifeMoments, setLifeMoments] = useState([]);
  const [lifeMomentTypes, setLifeMomentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCapsule, setSelectedCapsule] = useState(null);
  const [capsuleContents, setCapsuleContents] = useState([]);
  
  // Modal states
  const [showCreateCapsule, setShowCreateCapsule] = useState(false);
  const [showAddContent, setShowAddContent] = useState(false);
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
  const [showLifeMoment, setShowLifeMoment] = useState(false);
  const [showVideoGoodbye, setShowVideoGoodbye] = useState(false);
  
  // Form states
  const [newCapsule, setNewCapsule] = useState({ title: '', description: '', capsule_type: 'legacy' });
  const [newContent, setNewContent] = useState({ content_type: 'text', content_data: '', title: '' });
  const [newBeneficiary, setNewBeneficiary] = useState({ name: '', email: '', relationship: '', personal_message: '' });
  const [newMoment, setNewMoment] = useState({
    moment_type: 'birthday', title: '', date: '', content: '',
    beneficiary_ids: [], is_recurring: false, recurrence_years: 5
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [capsulesRes, statsRes, momentsRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/timecapsule/`, { withCredentials: true }),
        axios.get(`${API_URL}/timecapsule/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/timecapsule/life-moments`, { withCredentials: true }),
        axios.get(`${API_URL}/timecapsule/life-moment-types`, { withCredentials: true }),
      ]);
      
      setCapsules(capsulesRes.data.capsules || []);
      setStats(statsRes.data);
      setLifeMoments(momentsRes.data.moments || []);
      setLifeMomentTypes(typesRes.data.types || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCapsuleContents = async (capsuleId) => {
    try {
      const response = await axios.get(`${API_URL}/timecapsule/${capsuleId}/content`, { withCredentials: true });
      setCapsuleContents(response.data.contents || []);
    } catch (error) {
      toast.error('Failed to load capsule contents');
    }
  };

  const selectCapsule = async (capsule) => {
    setSelectedCapsule(capsule);
    await fetchCapsuleContents(capsule.capsule_id);
  };

  const createCapsule = async () => {
    try {
      const response = await axios.post(`${API_URL}/timecapsule/`, newCapsule, { withCredentials: true });
      toast.success('TimeCapsule created! 🎁');
      setShowCreateCapsule(false);
      setNewCapsule({ title: '', description: '', capsule_type: 'legacy' });
      fetchData();
      selectCapsule(response.data.capsule);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create capsule');
    }
  };

  const addContent = async () => {
    if (!selectedCapsule) return;
    try {
      await axios.post(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}/content`, newContent, { withCredentials: true });
      toast.success('Content added and encrypted! 🔐');
      setShowAddContent(false);
      setNewContent({ content_type: 'text', content_data: '', title: '' });
      fetchCapsuleContents(selectedCapsule.capsule_id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add content');
    }
  };

  const deleteContent = async (contentId) => {
    if (!selectedCapsule || !window.confirm('Delete this content?')) return;
    try {
      await axios.delete(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}/content/${contentId}`, { withCredentials: true });
      toast.success('Content deleted');
      fetchCapsuleContents(selectedCapsule.capsule_id);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const addBeneficiary = async () => {
    if (!selectedCapsule) return;
    try {
      await axios.post(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}/beneficiaries`, newBeneficiary, { withCredentials: true });
      toast.success(`${newBeneficiary.name} added as beneficiary! 💝`);
      setShowAddBeneficiary(false);
      setNewBeneficiary({ name: '', email: '', relationship: '', personal_message: '' });
      fetchData();
      // Refresh selected capsule
      const response = await axios.get(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}`, { withCredentials: true });
      setSelectedCapsule(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add beneficiary');
    }
  };

  const removeBeneficiary = async (beneficiaryId) => {
    if (!selectedCapsule || !window.confirm('Remove this beneficiary?')) return;
    try {
      await axios.delete(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}/beneficiaries/${beneficiaryId}`, { withCredentials: true });
      toast.success('Beneficiary removed');
      fetchData();
      const response = await axios.get(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}`, { withCredentials: true });
      setSelectedCapsule(response.data);
    } catch (error) {
      toast.error('Failed to remove beneficiary');
    }
  };

  const sealCapsule = async () => {
    if (!selectedCapsule) return;
    const confirmed = window.confirm(
      '⚠️ SEAL THIS CAPSULE?\n\n' +
      'Once sealed, you cannot edit the contents anymore.\n' +
      'The capsule will only be released to your beneficiaries.\n\n' +
      'Are you sure you want to seal this TimeCapsule?'
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}/seal`, {}, { withCredentials: true });
      toast.success('TimeCapsule sealed! 🔒');
      fetchData();
      setSelectedCapsule(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seal capsule');
    }
  };

  const deleteCapsule = async () => {
    if (!selectedCapsule) return;
    const confirmed = window.confirm(
      '⚠️ DELETE THIS CAPSULE?\n\n' +
      'This will permanently delete the capsule and ALL its contents.\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure?'
    );
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API_URL}/timecapsule/${selectedCapsule.capsule_id}`, { withCredentials: true });
      toast.success('TimeCapsule deleted');
      setSelectedCapsule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete capsule');
    }
  };

  const createLifeMoment = async () => {
    try {
      await axios.post(`${API_URL}/timecapsule/life-moments`, newMoment, { withCredentials: true });
      toast.success('Life moment scheduled! 🎉');
      setShowLifeMoment(false);
      setNewMoment({
        moment_type: 'birthday', title: '', date: '', content: '',
        beneficiary_ids: [], is_recurring: false, recurrence_years: 5
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create life moment');
    }
  };

  const getContentIcon = (type) => {
    const icons = { text: FileText, photo: Image, video: Video, voice: Mic, document: FileText };
    return icons[type] || FileText;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">Loading your TimeCapsules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 flex items-center justify-center shadow-lg">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white">
                TimeCapsule
              </h1>
              <p className="text-gray-600 dark:text-gray-400">Your Digital Legacy</p>
            </div>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Create encrypted time capsules with messages, photos, and videos for your loved ones.
            Only released to your beneficiaries when you choose.
          </p>
        </motion.div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Capsules', value: stats.total_capsules, icon: Lock, color: 'purple' },
              { label: 'Beneficiaries', value: stats.total_beneficiaries, icon: Users, color: 'pink' },
              { label: 'Content Items', value: stats.total_content_items, icon: FileText, color: 'blue' },
              { label: 'Life Moments', value: stats.scheduled_life_moments, icon: Calendar, color: 'amber' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={`p-4 rounded-2xl bg-${stat.color}-50 dark:bg-${stat.color}-900/20 border border-${stat.color}-200 dark:border-${stat.color}-800`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color}-500 mb-2`} />
                  <p className={`text-3xl font-bold text-${stat.color}-600 dark:text-${stat.color}-400`}>{stat.value}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="capsules" className="w-full">
          <TabsList className="grid w-full grid-cols-3 rounded-2xl p-1 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <TabsTrigger value="capsules" className="rounded-xl font-bold" data-testid="capsules-tab">
              <Lock className="w-4 h-4 mr-2" />
              My Capsules
            </TabsTrigger>
            <TabsTrigger value="life-moments" className="rounded-xl font-bold" data-testid="moments-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Life Moments
            </TabsTrigger>
            <TabsTrigger value="how-it-works" className="rounded-xl font-bold" data-testid="how-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              How It Works
            </TabsTrigger>
          </TabsList>

          {/* Capsules Tab */}
          <TabsContent value="capsules">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Capsule List */}
              <div className="lg:col-span-1 space-y-4">
                <Button
                  onClick={() => setShowCreateCapsule(true)}
                  className="w-full rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold h-14 shadow-lg"
                  data-testid="create-capsule-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create New Capsule
                </Button>

                {capsules.length === 0 ? (
                  <Card className="p-8 rounded-3xl text-center bg-white/70 dark:bg-gray-800/70 border border-white/20">
                    <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No capsules yet</p>
                    <p className="text-sm text-gray-500 mt-2">Create your first TimeCapsule to get started</p>
                  </Card>
                ) : (
                  capsules.map((capsule) => (
                    <motion.div
                      key={capsule.capsule_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => selectCapsule(capsule)}
                      className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${
                        selectedCapsule?.capsule_id === capsule.capsule_id
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-white/20 bg-white/70 dark:bg-gray-800/70 hover:border-purple-300'
                      }`}
                      data-testid={`capsule-${capsule.capsule_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          capsule.is_sealed
                            ? 'bg-amber-100 dark:bg-amber-900/30'
                            : 'bg-purple-100 dark:bg-purple-900/30'
                        }`}>
                          {capsule.is_sealed ? (
                            <Shield className="w-6 h-6 text-amber-600" />
                          ) : (
                            <Lock className="w-6 h-6 text-purple-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate">{capsule.title}</h3>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{capsule.content_count} items</span>
                            <span>•</span>
                            <span>{capsule.beneficiaries?.length || 0} beneficiaries</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                      {capsule.is_sealed && (
                        <Badge className="mt-2 bg-amber-100 text-amber-700">Sealed</Badge>
                      )}
                    </motion.div>
                  ))
                )}
              </div>

              {/* Capsule Detail */}
              <div className="lg:col-span-2">
                {selectedCapsule ? (
                  <Card className="rounded-3xl overflow-hidden bg-white/70 dark:bg-gray-800/70 border border-white/20 shadow-xl">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 p-6 text-white">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-2xl font-bold">{selectedCapsule.title}</h2>
                          <p className="text-white/80 mt-1">{selectedCapsule.description || 'No description'}</p>
                        </div>
                        <Badge className={`${selectedCapsule.is_sealed ? 'bg-amber-500' : 'bg-white/20'} text-white`}>
                          {selectedCapsule.is_sealed ? 'Sealed' : 'Active'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-4 text-sm text-white/80">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {selectedCapsule.content_count} items
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {selectedCapsule.beneficiaries?.length || 0} beneficiaries
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Created {new Date(selectedCapsule.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    {!selectedCapsule.is_sealed && (
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                        <Button
                          onClick={() => setShowAddContent(true)}
                          variant="outline"
                          className="rounded-full"
                          data-testid="add-content-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Content
                        </Button>
                        <Button
                          onClick={() => setShowAddBeneficiary(true)}
                          variant="outline"
                          className="rounded-full"
                          data-testid="add-beneficiary-btn"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Add Beneficiary
                        </Button>
                        <Button
                          onClick={() => setShowVideoGoodbye(true)}
                          variant="outline"
                          className="rounded-full"
                          data-testid="video-goodbye-btn"
                        >
                          <Video className="w-4 h-4 mr-1" />
                          Video Goodbye
                        </Button>
                        <div className="flex-1" />
                        <Button
                          onClick={sealCapsule}
                          variant="outline"
                          className="rounded-full border-amber-500 text-amber-600 hover:bg-amber-50"
                          data-testid="seal-btn"
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Seal Capsule
                        </Button>
                        <Button
                          onClick={deleteCapsule}
                          variant="outline"
                          className="rounded-full border-red-500 text-red-600 hover:bg-red-50"
                          data-testid="delete-capsule-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {/* Content List */}
                    <div className="p-6">
                      <h3 className="font-bold text-gray-900 dark:text-white mb-4">Contents</h3>
                      {capsuleContents.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No content yet. Add your first message, photo, or video.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {capsuleContents.map((content) => {
                            const ContentIcon = getContentIcon(content.content_type);
                            return (
                              <div
                                key={content.content_id}
                                className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                              >
                                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                  <ContentIcon className="w-5 h-5 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 dark:text-white">
                                    {content.title || content.content_type}
                                  </p>
                                  {content.content_type === 'text' && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                      {content.content}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1">
                                    Added {new Date(content.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                {!selectedCapsule.is_sealed && (
                                  <Button
                                    onClick={() => deleteContent(content.content_id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Beneficiaries */}
                      <h3 className="font-bold text-gray-900 dark:text-white mt-8 mb-4">Beneficiaries</h3>
                      {(!selectedCapsule.beneficiaries || selectedCapsule.beneficiaries.length === 0) ? (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No beneficiaries added. Add loved ones who will receive this capsule.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {selectedCapsule.beneficiaries.map((ben) => (
                            <div
                              key={ben.beneficiary_id}
                              className="flex items-center gap-3 p-4 bg-pink-50 dark:bg-pink-900/20 rounded-xl"
                            >
                              <div className="w-10 h-10 rounded-full bg-pink-200 dark:bg-pink-800 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-pink-600" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 dark:text-white">{ben.name}</p>
                                <p className="text-sm text-gray-500">{ben.email} • {ben.relationship}</p>
                              </div>
                              {!selectedCapsule.is_sealed && (
                                <Button
                                  onClick={() => removeBeneficiary(ben.beneficiary_id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                ) : (
                  <Card className="rounded-3xl p-12 text-center bg-white/70 dark:bg-gray-800/70 border border-white/20">
                    <Lock className="w-20 h-20 text-gray-200 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Select a Capsule</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Choose a capsule from the list or create a new one to get started
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Life Moments Tab */}
          <TabsContent value="life-moments">
            <div className="space-y-6">
              <Button
                onClick={() => setShowLifeMoment(true)}
                className="rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold h-14 shadow-lg"
                data-testid="create-moment-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule a Life Moment
              </Button>

              {lifeMoments.length === 0 ? (
                <Card className="p-12 rounded-3xl text-center bg-white/70 dark:bg-gray-800/70">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Life Moments Scheduled</h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    Schedule messages to be delivered on special occasions like birthdays, anniversaries, or graduations.
                  </p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lifeMoments.map((moment) => {
                    const MomentIcon = MOMENT_ICONS[moment.moment_type] || Calendar;
                    return (
                      <Card
                        key={moment.moment_id}
                        className="p-6 rounded-2xl bg-white/70 dark:bg-gray-800/70 border border-white/20"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <MomentIcon className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{moment.title}</h3>
                            <p className="text-sm text-gray-500">{moment.moment_type}</p>
                          </div>
                        </div>
                        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                          <p>📅 {new Date(moment.scheduled_date).toLocaleDateString()}</p>
                          {moment.is_recurring && (
                            <p className="text-amber-600 mt-1">🔄 Recurring yearly</p>
                          )}
                        </div>
                        <Badge className={`mt-3 ${
                          moment.status === 'scheduled' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {moment.status}
                        </Badge>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* How It Works Tab */}
          <TabsContent value="how-it-works">
            <Card className="rounded-3xl p-8 bg-white/70 dark:bg-gray-800/70 border border-white/20">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How TimeCapsule Works</h2>
              
              <div className="space-y-8">
                {[
                  {
                    step: 1,
                    title: 'Create Your Capsule',
                    description: 'Start by creating a TimeCapsule. Give it a meaningful title and description.',
                    icon: Plus
                  },
                  {
                    step: 2,
                    title: 'Add Your Memories',
                    description: 'Fill it with text messages, photos, videos, voice recordings, or documents. Everything is encrypted.',
                    icon: FileText
                  },
                  {
                    step: 3,
                    title: 'Add Beneficiaries',
                    description: 'Choose who will receive your capsule - family members, friends, or loved ones.',
                    icon: Users
                  },
                  {
                    step: 4,
                    title: 'Seal When Ready',
                    description: 'When you\'re satisfied, seal the capsule. No more changes can be made after sealing.',
                    icon: Shield
                  },
                  {
                    step: 5,
                    title: 'Secure Release',
                    description: 'Your beneficiaries can request release by providing documentation. Our team verifies before releasing.',
                    icon: Unlock
                  }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <item.icon className="w-5 h-5 text-purple-500" />
                        {item.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
                <h3 className="font-bold text-purple-800 dark:text-purple-200 flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5" />
                  Security Promise
                </h3>
                <p className="text-purple-700 dark:text-purple-300 text-sm">
                  All content is encrypted using military-grade AES-256 encryption. Only your designated beneficiaries 
                  can decrypt and access the contents after proper verification. Not even Socyy staff can read your messages.
                </p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <AnimatePresence>
          {/* Create Capsule Modal */}
          {showCreateCapsule && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowCreateCapsule(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-purple-500" />
                  Create TimeCapsule
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={newCapsule.title}
                      onChange={(e) => setNewCapsule({ ...newCapsule, title: e.target.value })}
                      placeholder="Messages for my children"
                      className="rounded-xl mt-1"
                      data-testid="capsule-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newCapsule.description}
                      onChange={(e) => setNewCapsule({ ...newCapsule, description: e.target.value })}
                      placeholder="What this capsule contains..."
                      className="rounded-xl mt-1"
                      rows={3}
                      data-testid="capsule-desc-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowCreateCapsule(false)} variant="outline" className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button onClick={createCapsule} className="flex-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" data-testid="confirm-create-capsule">
                      Create Capsule
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Add Content Modal */}
          {showAddContent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowAddContent(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Add Content</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Content Type</Label>
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {['text', 'photo', 'video', 'voice', 'document'].map((type) => {
                        const Icon = getContentIcon(type);
                        return (
                          <button
                            key={type}
                            onClick={() => setNewContent({ ...newContent, content_type: type })}
                            className={`p-3 rounded-xl border-2 transition-all ${
                              newContent.content_type === type
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                                : 'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <Icon className={`w-5 h-5 mx-auto ${
                              newContent.content_type === type ? 'text-purple-600' : 'text-gray-500'
                            }`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>Title (optional)</Label>
                    <Input
                      value={newContent.title}
                      onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                      placeholder="A message for..."
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>{newContent.content_type === 'text' ? 'Message' : 'URL/Content'}</Label>
                    {newContent.content_type === 'text' ? (
                      <Textarea
                        value={newContent.content_data}
                        onChange={(e) => setNewContent({ ...newContent, content_data: e.target.value })}
                        placeholder="Write your message here..."
                        className="rounded-xl mt-1"
                        rows={5}
                        data-testid="content-text-input"
                      />
                    ) : (
                      <Input
                        value={newContent.content_data}
                        onChange={(e) => setNewContent({ ...newContent, content_data: e.target.value })}
                        placeholder="Paste URL or base64 content"
                        className="rounded-xl mt-1"
                      />
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowAddContent(false)} variant="outline" className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button onClick={addContent} className="flex-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" data-testid="confirm-add-content">
                      Add & Encrypt
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Add Beneficiary Modal */}
          {showAddBeneficiary && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowAddBeneficiary(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-500" />
                  Add Beneficiary
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={newBeneficiary.name}
                      onChange={(e) => setNewBeneficiary({ ...newBeneficiary, name: e.target.value })}
                      placeholder="John Doe"
                      className="rounded-xl mt-1"
                      data-testid="beneficiary-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newBeneficiary.email}
                      onChange={(e) => setNewBeneficiary({ ...newBeneficiary, email: e.target.value })}
                      placeholder="john@example.com"
                      className="rounded-xl mt-1"
                      data-testid="beneficiary-email-input"
                    />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <select
                      value={newBeneficiary.relationship}
                      onChange={(e) => setNewBeneficiary({ ...newBeneficiary, relationship: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 dark:border-gray-600 p-3 mt-1 bg-white dark:bg-gray-700"
                    >
                      <option value="">Select relationship</option>
                      <option value="spouse">Spouse/Partner</option>
                      <option value="child">Child</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="grandchild">Grandchild</option>
                      <option value="friend">Friend</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label>Personal Message (optional)</Label>
                    <Textarea
                      value={newBeneficiary.personal_message}
                      onChange={(e) => setNewBeneficiary({ ...newBeneficiary, personal_message: e.target.value })}
                      placeholder="A special message for this person..."
                      className="rounded-xl mt-1"
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowAddBeneficiary(false)} variant="outline" className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button onClick={addBeneficiary} className="flex-1 rounded-full bg-gradient-to-r from-pink-500 to-rose-500" data-testid="confirm-add-beneficiary">
                      Add Beneficiary
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Life Moment Modal */}
          {showLifeMoment && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowLifeMoment(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-amber-500" />
                  Schedule Life Moment
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>Moment Type</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {lifeMomentTypes.slice(0, 9).map((type) => {
                        const Icon = MOMENT_ICONS[type.id] || Calendar;
                        return (
                          <button
                            key={type.id}
                            onClick={() => setNewMoment({ ...newMoment, moment_type: type.id })}
                            className={`p-3 rounded-xl border-2 transition-all text-center ${
                              newMoment.moment_type === type.id
                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                                : 'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <span className="text-2xl block mb-1">{type.icon}</span>
                            <span className="text-xs">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={newMoment.title}
                      onChange={(e) => setNewMoment({ ...newMoment, title: e.target.value })}
                      placeholder="Happy Birthday Message"
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Scheduled Date</Label>
                    <Input
                      type="date"
                      value={newMoment.date}
                      onChange={(e) => setNewMoment({ ...newMoment, date: e.target.value })}
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea
                      value={newMoment.content}
                      onChange={(e) => setNewMoment({ ...newMoment, content: e.target.value })}
                      placeholder="Your message for this special moment..."
                      className="rounded-xl mt-1"
                      rows={4}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="recurring"
                      checked={newMoment.is_recurring}
                      onChange={(e) => setNewMoment({ ...newMoment, is_recurring: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="recurring" className="mb-0">Repeat yearly</Label>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowLifeMoment(false)} variant="outline" className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button onClick={createLifeMoment} className="flex-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500" data-testid="confirm-life-moment">
                      Schedule Moment
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <FloatingDock user={user} />
    </div>
  );
};

export default TimeCapsule;
